<template>
  <div class="code-viewer">
    <div class="code-header">
      <div class="code-title-section" @click="toggleExpanded">
        <h3 class="code-title">💻 测试代码</h3>
        <button class="expand-toggle-button" @click.stop="toggleExpanded">
          <span class="expand-icon">{{ isExpanded ? '▼' : '▶' }}</span>
          <span class="expand-text">{{ isExpanded ? '收起' : '展开' }}</span>
        </button>
      </div>
      <div class="code-actions" @click.stop>
        <div class="language-buttons">
          <button
            v-for="lang in availableLanguages"
            :key="lang"
            class="language-button"
            :class="{ active: selectedLanguage === lang }"
            @click="selectedLanguage = lang"
            :title="languageLabels[lang]"
          >
            {{ languageLabels[lang] }}
          </button>
        </div>
        <div class="action-buttons">
          <button @click="copyCode" class="action-button" :disabled="copying">
            {{ copying ? '✓ 已复制' : '📋 复制' }}
          </button>
          <button @click="downloadCode" class="action-button">
            💾 下载
          </button>
        </div>
      </div>
    </div>
    
    <div v-show="isExpanded" class="code-container">
      <div class="code-wrapper">
        <div class="line-numbers">
          <div 
            v-for="n in lineCount" 
            :key="n" 
            class="line-number"
          >
            {{ n }}
          </div>
        </div>
        <pre><code 
          :class="`language-${languageClass}`"
          ref="codeEl"
        ></code></pre>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import Prism from 'prismjs'
import { useToast } from '@/composables/useToast'

const toast = useToast()

const props = defineProps({
  templates: {
    type: Object,
    required: true
  },
  defaultLanguage: {
    type: String,
    default: 'python'
  },
  filename: {
    type: String,
    default: 'algorithm'
  },
  toyName: {
    type: String,
    default: ''
  }
})

const selectedLanguage = ref(props.defaultLanguage)
const copying = ref(false)
const isExpanded = ref(false)  // 默认收起

function toggleExpanded() {
  isExpanded.value = !isExpanded.value
}

const languageLabels = {
  python: 'Python',
  javascript: 'JavaScript',
  matlab: 'MATLAB',
  r: 'R'
}

const languageExtensions = {
  python: 'py',
  javascript: 'js',
  matlab: 'm',
  r: 'r'
}

const languageClass = computed(() => {
  // Prism uses 'js' for JavaScript
  if (selectedLanguage.value === 'javascript') return 'js'
  return selectedLanguage.value
})

const availableLanguages = computed(() => 
  Object.keys(props.templates).filter(lang => props.templates[lang])
)

const currentCode = computed(() => 
  props.templates[selectedLanguage.value] || ''
)

const lineCount = computed(() => {
  if (!currentCode.value) return 0
  // 计算行数，包括空行
  const lines = currentCode.value.split('\n')
  return lines.length
})

const codeEl = ref(null)

function highlightNow() {
  if (!codeEl.value) return
  const preEl = codeEl.value.parentNode
  codeEl.value.textContent = currentCode.value || ''
  try {
    Prism.highlightElement(codeEl.value)
  } catch (error) {
    console.error('Error highlighting code element:', error)
  }
}

function escapeHtml(text) {
  const div = document.createElement('div')
  div.textContent = text
  return div.innerHTML
}

function onLanguageChange() {
  // 可以在这里添加语言切换的额外逻辑
}

async function copyCode() {
  try {
    await navigator.clipboard.writeText(currentCode.value)
    copying.value = true
    toast.success('代码已复制到剪贴板', '复制成功')
    setTimeout(() => {
      copying.value = false
    }, 2000)
  } catch (error) {
    console.error('Failed to copy code:', error)
    // 降级方案：使用旧的复制方法
    fallbackCopyCode()
  }
}

function fallbackCopyCode() {
  const textarea = document.createElement('textarea')
  textarea.value = currentCode.value
  textarea.style.position = 'fixed'
  textarea.style.opacity = '0'
  document.body.appendChild(textarea)
  textarea.select()
  
  try {
    document.execCommand('copy')
    copying.value = true
    toast.success('代码已复制到剪贴板', '复制成功')
    setTimeout(() => {
      copying.value = false
    }, 2000)
  } catch (error) {
    console.error('Fallback copy failed:', error)
    toast.error('复制失败，请手动复制代码', '复制失败')
  } finally {
    document.body.removeChild(textarea)
  }
}

function downloadCode() {
  try {
    const blob = new Blob([currentCode.value], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    const extension = languageExtensions[selectedLanguage.value] || 'txt'
    
    // 生成文件名：如果提供了 toyName，使用 [toy-name]-algorithm.[ext]，否则使用原来的逻辑
    const filename = props.toyName 
      ? `${props.toyName}-algorithm.${extension}`
      : `${props.filename}.${extension}`
    
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    
    toast.success(`文件已下载: ${filename}`, '下载成功')
  } catch (error) {
    console.error('Failed to download code:', error)
    toast.error('下载失败，请重试', '下载失败')
  }
}

// 确保在模板可用时选择第一个可用语言
watch(() => props.templates, (newTemplates) => {
  const available = Object.keys(newTemplates).filter(lang => newTemplates[lang])
  if (available.length > 0 && !newTemplates[selectedLanguage.value]) {
    selectedLanguage.value = available[0]
  }
}, { immediate: true })

onMounted(() => {
  // 确保选择的语言在可用语言中
  if (!availableLanguages.value.includes(selectedLanguage.value)) {
    selectedLanguage.value = availableLanguages.value[0] || 'python'
  }
  // 如果展开，才高亮代码
  if (isExpanded.value) {
    highlightNow()
  }
})

// 当展开时，高亮代码
watch(isExpanded, (expanded) => {
  if (expanded) {
    nextTick(() => {
      highlightNow()
    })
  }
})

watch([languageClass, currentCode], () => {
  // 只有在展开时才高亮代码
  if (isExpanded.value) {
    highlightNow()
  }
})
</script>

<style scoped>
.code-viewer {
  width: 100%;
}

.code-header {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 0.75rem;
  border-radius: 6px;
  transition: background-color 0.2s;
  position: relative;
}

.code-title-section {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  position: absolute;
  left: 0.75rem;
}

.code-title-section:hover {
  opacity: 0.8;
}

.code-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #333;
  margin: 0;
  white-space: nowrap;
}

.expand-toggle-button {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.625rem;
  background-color: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 0.8125rem;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.expand-toggle-button:hover {
  background-color: #e8e8e8;
  border-color: #1976d2;
  color: #1976d2;
}

.expand-icon {
  font-size: 0.75rem;
  transition: transform 0.2s;
}

.expand-text {
  font-weight: 500;
}

.code-actions {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
  flex: 1;
}

.code-actions .action-buttons {
  position: absolute;
  right: 0.75rem;
}

.language-buttons {
  display: flex;
  gap: 0.3125rem;
  flex-shrink: 0;
}

.language-button {
  display: flex;
  align-items: center;
  padding: 0.3125rem 0.5rem;
  background-color: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 0.6875rem;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  min-height: 24px;
}

.language-button:hover {
  background-color: #e8e8e8;
  border-color: #1976d2;
  color: #1976d2;
}

.language-button.active {
  background-color: #1976d2;
  border-color: #1976d2;
  color: white;
  font-weight: 500;
}

.language-button.active:hover {
  background-color: #1565c0;
  border-color: #1565c0;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.action-button {
  padding: 0.5rem 1rem;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #555;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.action-button:hover:not(:disabled) {
  background: #f5f5f5;
  border-color: #bbb;
}

.action-button:active:not(:disabled) {
  transform: scale(0.98);
}

.action-button:disabled {
  color: #4CAF50;
  border-color: #4CAF50;
  cursor: default;
}

.code-container {
  background: #2d2d2d;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.code-wrapper {
  display: flex;
  overflow: hidden;
}

.line-numbers {
  background: #1e1e1e;
  border-right: 1px solid #333;
  padding: 1.5rem 0.5rem;
  text-align: right;
  user-select: none;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 0.875rem;
  line-height: 1.6;
  color: #888;
  min-width: 40px;
  flex-shrink: 0;
}

.line-number {
  min-height: 1.6em;
}

.code-container pre {
  margin: 0;
  padding: 1.5rem;
  overflow-x: auto;
  font-size: 0.875rem;
  line-height: 1.6;
  flex: 1;
  background: #2d2d2d;
}

.code-container code {
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  color: #ccc;
  white-space: pre;
  display: block;
}

/* 自定义滚动条 */
.code-container pre::-webkit-scrollbar {
  height: 8px;
}

.code-container pre::-webkit-scrollbar-track {
  background: #1e1e1e;
}

.code-container pre::-webkit-scrollbar-thumb {
  background: #555;
  border-radius: 4px;
}

.code-container pre::-webkit-scrollbar-thumb:hover {
  background: #666;
}

/* 平板设备 */
@media (max-width: 1024px) and (min-width: 769px) {
  .code-container pre,
  .line-numbers {
    font-size: 0.85rem;
    padding: 1.25rem;
  }
  .line-numbers {
    padding-right: 0.5rem;
  }
}

/* 移动端优化 - 增大触摸目标 */
@media (max-width: 768px) {
  .code-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.875rem;
    position: relative;
  }

  .code-title-section {
    position: static;
    width: 100%;
    justify-content: space-between;
  }

  .code-actions .action-buttons {
    position: static;
  }

  .code-title {
    font-size: 1rem;
  }

  .expand-toggle-button {
    min-height: 44px;
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
  }

  .code-actions {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }

  .language-buttons {
    width: 100%;
    flex-wrap: wrap;
    gap: 0.375rem;
  }

  .language-button {
    flex: 1;
    min-width: 80px;
    min-height: 44px;
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
    -webkit-tap-highlight-color: transparent;
    justify-content: center;
  }

  .action-buttons {
    width: 100%;
    gap: 0.5rem;
  }

  .action-button {
    flex: 1;
    min-height: 44px;
    padding: 0.75rem 1rem;
    font-size: 0.9375rem;
    -webkit-tap-highlight-color: transparent;
  }

  .code-container {
    border-radius: 6px;
  }

  .code-container pre,
  .line-numbers {
    padding: 1rem;
    font-size: 0.8125rem;
    line-height: 1.5;
  }
  .line-numbers {
    padding-right: 0.5rem;
    min-width: 35px;
  }

  /* 优化移动端滚动 */
  .code-container pre {
    -webkit-overflow-scrolling: touch;
  }
}

/* 小屏幕移动设备 */
@media (max-width: 480px) {
  .code-header {
    gap: 0.5rem;
    padding: 0.75rem;
  }

  .code-title-section {
    gap: 0.5rem;
  }

  .expand-toggle-button {
    min-height: 40px;
    padding: 0.5rem 0.625rem;
    font-size: 0.8125rem;
  }

  .code-actions {
    gap: 0.5rem;
  }

  .language-buttons {
    gap: 0.25rem;
  }

  .language-button {
    min-width: 70px;
    padding: 0.5rem 0.625rem;
    font-size: 0.8125rem;
    min-height: 40px;
  }

  .action-button {
    min-width: 90px;
    padding: 0.625rem 0.75rem;
    font-size: 0.875rem;
    min-height: 40px;
  }

  .code-container pre,
  .line-numbers {
    padding: 0.875rem;
    font-size: 0.75rem;
  }
  .line-numbers {
    padding-right: 0.5rem;
    min-width: 30px;
  }
}
</style>
